import { useCallback, useEffect, useState } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import { useTheme } from '../../hooks/useTheme';
import { FALLBACK_NAV } from '../../config/nav';

const SIDEBAR_KEY = 'tmr-sidebar-state';
const DESKTOP_BP = 992;

function loadSidebarState() {
  try {
    const v = localStorage.getItem(SIDEBAR_KEY);
    if (v === 'expanded' || v === 'collapsed' || v === 'hidden') return v;
  } catch (_) {}
  return 'expanded';
}

function nextSidebarState(current) {
  if (current === 'expanded') return 'collapsed';
  if (current === 'collapsed') return 'hidden';
  return 'expanded';
}

export default function AppShell({ children, activeKey = 'dashboard', username = 'User', fullWidth = false }) {
  const { theme, toggle } = useTheme();
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > DESKTOP_BP);
  const [sidebarState, setSidebarState] = useState(loadSidebarState);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sections, setSections] = useState(FALLBACK_NAV.sections);

  const persistSidebarState = useCallback((next) => {
    setSidebarState(next);
    try { localStorage.setItem(SIDEBAR_KEY, next); } catch (_) {}
  }, []);

  useEffect(() => {
    fetch(`/api/api_nav.php?active=${activeKey}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.sections?.length) setSections(data.sections);
      })
      .catch(() => {});
  }, [activeKey]);

  useEffect(() => {
    const onResize = () => {
      const desktop = window.innerWidth > DESKTOP_BP;
      setIsDesktop(desktop);
      if (desktop) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const body = document.body;
    body.classList.toggle('tmr-nav-open', !isDesktop && mobileOpen);
    body.classList.toggle('tmr-sidebar-docked', isDesktop);
    body.classList.remove('tmr-sidebar-expanded', 'tmr-sidebar-collapsed', 'tmr-sidebar-hidden');
    if (isDesktop) body.classList.add(`tmr-sidebar-${sidebarState}`);
    return () => {
      body.classList.remove(
        'tmr-nav-open',
        'tmr-sidebar-docked',
        'tmr-sidebar-expanded',
        'tmr-sidebar-collapsed',
        'tmr-sidebar-hidden',
      );
    };
  }, [isDesktop, mobileOpen, sidebarState]);

  const handleToggleSidebar = () => {
    if (isDesktop) {
      persistSidebarState(nextSidebarState(sidebarState));
    } else {
      setMobileOpen(o => !o);
    }
  };

  const handleSidebarEdgeToggle = () => {
    if (!isDesktop) return;
    persistSidebarState(sidebarState === 'expanded' ? 'collapsed' : 'expanded');
  };

  const handleSidebarHide = () => {
    if (!isDesktop) return;
    persistSidebarState('hidden');
  };

  const handleSidebarReopen = () => {
    if (!isDesktop) return;
    persistSidebarState('expanded');
  };

  return (
    <>
      <Header
        username={username}
        theme={theme}
        onToggleTheme={toggle}
        onToggleSidebar={handleToggleSidebar}
        sidebarOpen={isDesktop ? sidebarState !== 'hidden' : mobileOpen}
        sidebarDocked={isDesktop}
        sidebarState={sidebarState}
      />
      <Sidebar
        docked={isDesktop}
        state={sidebarState}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onEdgeToggle={handleSidebarEdgeToggle}
        onHide={handleSidebarHide}
        sections={sections}
        activeKey={activeKey}
      />
      {isDesktop && sidebarState === 'hidden' && (
        <button
          type="button"
          className="tmr-sidebar-reopen-tab"
          onClick={handleSidebarReopen}
          title="Open navigation"
          aria-label="Open navigation panel"
        >
          <i className="bi bi-chevron-right" />
        </button>
      )}
      <main className="tmr-main">
        <div className={fullWidth ? 'obsidian-full' : 'tmr-container obsidian-dashboard'}>
          {children}
        </div>
      </main>
    </>
  );
}